import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { FaInstagram } from "react-icons/fa";
import { Globe, ArrowUpRight } from "lucide-react";

const API = "/api";

const INTERESTS = ["Instagram", "TikTok", "YouTube", "Spotify", "Facebook", "Targeted bundle", "Something else"];

const inputCls =
  "w-full border border-[#1F1F1F] bg-[#0D0D0D] px-4 py-3.5 text-sm text-white placeholder-[#555555] outline-none transition-colors duration-300 focus:border-[#E11D2E]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, interest: form.interest || null });
      toast.success("Enquiry received. We reply within one business day.");
      setForm({ name: "", email: "", interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Check the fields and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[#1F1F1F] bg-[#050505] py-28 lg:py-40">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E11D2E]">Contact</p>
          <h2 className="mt-4 font-display text-3xl font-800 leading-tight tracking-tight text-white sm:text-4xl">
            Put budget behind real growth.
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-[#8A8A8A] md:text-base">
            Tell us the network, the target, and the timeline. We come back with a delivery plan and
            exact pricing — not a sales deck.
          </p>
          <div className="mt-10 space-y-4">
            <a
              data-testid="contact-instagram-link"
              href="https://instagram.com/cybersmokemedia"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 font-mono text-sm text-[#8A8A8A] transition-colors duration-300 hover:text-white"
            >
              <FaInstagram size={16} className="text-[#E11D2E]" />
              @cybersmokemedia
              <ArrowUpRight size={14} className="opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </a>
            <a
              data-testid="contact-website-link"
              href="https://cybersmokemedia.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 font-mono text-sm text-[#8A8A8A] transition-colors duration-300 hover:text-white"
            >
              <Globe size={16} className="text-[#E11D2E]" />
              CyberSmokeMedia.com
              <ArrowUpRight size={14} className="opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="border border-[#1F1F1F] bg-[#0D0D0D] p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Name</label>
                <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Email</label>
                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@brand.com" className={inputCls} />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-interest" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">I'm interested in</label>
              <select id="contact-interest" data-testid="contact-interest-select" value={form.interest} onChange={set("interest")} className={inputCls}>
                <option value="">Select a network</option>
                {INTERESTS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Message</label>
              <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="Targets, timeline, budget range…" className={`${inputCls} resize-none`} />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-8 w-full bg-[#E11D2E] py-4 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#F13B47] hover:shadow-[0_0_32px_rgba(225,29,46,0.4)] disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
